import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import {
  getRankTracking, runRankTracking, runRankKeyword, rankKeywordSerp, updateRankSettings, addRankKeyword, deleteRankKeyword, rankBalance,
  rankCountries,
  type RankFrequency, type RankDeviceKind, type RankTrackerSettings, type RankKeyword,
  type RankSnapshot, type RankTrackingData, type DfsBalance, type SerpBreakdown, type RankCountry,
} from "../lib/authClient";
import { siteKey } from "../lib/siteData";
import { COUNTRIES, DEFAULT_COUNTRY } from "../lib/countries";
import { showAlert, showConfirm } from "../lib/dialog";
import { confirmDeletePopover } from "../lib/confirmPopover";
import { CityPicker, prettyLoc, compactLoc } from "./CityPicker";

// Organic Rank Tracker — tracks a site's Google organic positions for a set of keywords over time
// via DataForSEO. Tracker-wide country / device / depth / frequency; per-keyword city. The overview
// graphs average position over a date range; each keyword row expands to a multi-series chart where
// EVERY page/URL on the site that ranks for the term is its own line (so multiple ranking pages show
// on one graph over time). Data source of truth is the DB (server scheduler + on-demand runs).

interface Props { siteUrl?: string; siteBrand?: string; refreshToken?: number }

const FREQ_LABEL: Record<RankFrequency, string> = { daily: "Daily", weekly: "Weekly", monthly: "Monthly" };
const LOW_BALANCE_USD = 10;
const usd = (n: number) => n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });

// Distinct, colorblind-friendly palette for the per-URL series (assigned by order of appearance).
const SERIES_COLORS = [
  "#2563eb", "#16a34a", "#db2777", "#ea580c", "#7c3aed", "#0891b2",
  "#ca8a04", "#dc2626", "#4f46e5", "#059669", "#be123c", "#0284c7",
];
const AVG_COLOR = "#ea580c";

// Friendly labels for DataForSEO SERP element types (for the Page-1 SERP view). Unknown types are
// title-cased from their raw key.
const FEATURE_LABELS: Record<string, string> = {
  ai_overview: "AI Overview", local_pack: "Local Pack", people_also_ask: "People Also Ask",
  featured_snippet: "Featured Snippet", knowledge_graph: "Knowledge Panel", images: "Images",
  video: "Video", top_stories: "Top Stories", related_searches: "Related Searches",
  people_also_search: "People Also Search", shopping: "Shopping", carousel: "Carousel",
  answer_box: "Answer Box", twitter: "X (Twitter)", google_reviews: "Google Reviews",
  find_results_on: "Find Results On", recipes: "Recipes", jobs: "Jobs", map: "Map", events: "Events",
};
const featLabel = (t: string) => FEATURE_LABELS[t] ?? t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

// The stored URL is "host/path" (server-normalized). Show the path for a compact label; the home
// page shows "/". The clickable link reconstructs an https:// URL.
function pathLabel(url: string): string {
  const slash = url.indexOf("/");
  if (slash < 0) return "/";
  const p = url.slice(slash);
  return p === "" ? "/" : p;
}
function hrefFor(url: string): string { return url.startsWith("http") ? url : `https://${url}`; }
const dayKey = (iso: string) => (iso || "").slice(0, 10);
function fmtDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? (iso || "").slice(0, 10) : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
// Best (lowest) organic position among all of a snapshot's ranking URLs; null when not ranking.
function bestPos(s: RankSnapshot): number | null {
  if (!s.rankings.length) return null;
  return Math.min(...s.rankings.map(r => r.position));
}

interface SeriesInput { key: string; label: string; color: string; pts: { t: number; pos: number; iso: string }[] }
interface HoverPt { leftPct: number; topPct: number; label: string; date: string; pos: number; color: string }

// Inline SVG position chart with an INVERTED y-axis (position 1 at top, worse ranks lower). One line
// per series (a single "Average position" line for the overview; one line per ranking URL for a
// keyword). No charting dependency — matches the app's SVG viz. windowStart/windowEnd (ms) bound the
// visible range; windowEnd defaults to now (an in-past end supports "Previous Month" etc.).
function PositionChart({ series, windowStart, windowEnd, height = 150 }: { series: SeriesInput[]; windowStart?: number; windowEnd?: number; height?: number }) {
  const [hover, setHover] = useState<HoverPt | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const gridStroke = isDark ? "rgba(255,255,255,.09)" : "#eef2f6";
  const axisFill = isDark ? "#93a4c0" : "#94a3b8";

  const visible = series.filter(s => !hidden.has(s.key) && s.pts.length);
  const wStart = windowStart != null ? windowStart : -Infinity;
  const wEnd = windowEnd != null ? windowEnd : Date.now();
  const clipped = visible.map(s => ({ ...s, pts: s.pts.filter(p => !isNaN(p.t) && p.t >= wStart - 86400000 && p.t <= wEnd + 86400000).sort((a, b) => a.t - b.t) })).filter(s => s.pts.length);

  if (!series.length || !series.some(s => s.pts.length)) {
    return <div style={{ fontSize: 11.5, color: isDark ? "#7c8aa0" : "#94a3b8" }}>No data yet — run the tracker to start the trend.</div>;
  }

  const W = 1000, H = height, padL = 30, padR = 14, padT = 12, padB = 22;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const allPos = clipped.flatMap(s => s.pts.map(p => p.pos));
  const maxPos = Math.max(5, ...(allPos.length ? allPos : [5]));
  const topPos = 1;
  const allT = clipped.flatMap(s => s.pts.map(p => p.t));
  const MIN_SPAN = 10 * 86400000, dayPad = 0.5 * 86400000;
  const dataStart = allT.length ? Math.min(...allT) : wEnd - MIN_SPAN;
  const start = Math.max(wStart, Math.min(dataStart - dayPad, wEnd - MIN_SPAN));
  const domEnd = wEnd + dayPad;
  const x = (t: number) => padL + Math.max(0, Math.min(1, (t - start) / (domEnd - start))) * plotW;
  // Inverted: position 1 → top, maxPos → bottom.
  const y = (pos: number) => padT + ((pos - topPos) / Math.max(1, maxPos - topPos)) * plotH;
  const yTickVals = (() => {
    const ticks = new Set<number>([1]);
    ticks.add(Math.max(1, Math.round(maxPos / 2)));
    ticks.add(maxPos);
    return [...ticks].sort((a, b) => a - b);
  })();
  const xTicks = Array.from({ length: 5 }, (_, i) => start + (i / 4) * (domEnd - start));
  const fmtTick = (t: number) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const fmtFull = (iso: string) => { const d = new Date(iso); return isNaN(d.getTime()) ? iso.slice(0, 10) : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };
  const above = (hover?.topPct ?? 100) > 30;
  const hx = !hover ? "-50%" : hover.leftPct <= 14 ? "0%" : hover.leftPct >= 86 ? "-100%" : "-50%";

  return (
    <div>
      <div style={{ position: "relative" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", maxWidth: "100%" }} role="img" aria-label="Organic position over time">
          {yTickVals.map(v => (
            <g key={v}>
              <line x1={padL} y1={y(v)} x2={W - padR} y2={y(v)} stroke={gridStroke} strokeWidth={1} />
              <text x={padL - 5} y={y(v) + 3} textAnchor="end" fontSize={6.4} fill={axisFill} fontFamily="'Inter','Mulish',sans-serif">#{v}</text>
            </g>
          ))}
          {xTicks.map((t, i) => (
            <text key={i} x={x(t)} y={H - 7} textAnchor={i === 0 ? "start" : i === 4 ? "end" : "middle"} fontSize={6.4} fill={axisFill} fontFamily="'Inter','Mulish',sans-serif">{fmtTick(t)}</text>
          ))}
          {clipped.map(s => (
            <g key={s.key}>
              {s.pts.length > 1 && <polyline fill="none" stroke={s.color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" points={s.pts.map(p => `${x(p.t)},${y(p.pos)}`).join(" ")} />}
              {s.pts.map((p, i) => (
                <g key={i}>
                  <circle cx={x(p.t)} cy={y(p.pos)} r={2.6} fill={s.color} />
                  <circle
                    cx={x(p.t)} cy={y(p.pos)} r={9} fill="transparent" style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHover({ leftPct: (x(p.t) / W) * 100, topPct: (y(p.pos) / H) * 100, label: s.label, date: fmtFull(p.iso), pos: p.pos, color: s.color })}
                    onMouseLeave={() => setHover(null)}
                  />
                </g>
              ))}
            </g>
          ))}
        </svg>
        {hover && (
          <div style={{
            position: "absolute", left: `${hover.leftPct}%`, top: `${hover.topPct}%`,
            transform: `translate(${hx}, ${above ? "calc(-100% - 9px)" : "9px"})`,
            background: isDark ? "#0f1b30" : "#ffffff", color: isDark ? "#dbe3ee" : "#334155", border: `1px solid ${isDark ? "rgba(255,255,255,.14)" : "#e2e8f0"}`, borderRadius: 6,
            boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.5)" : "0 4px 14px rgba(15,23,42,.14)", padding: "5px 9px", fontSize: 11, lineHeight: 1.35,
            fontFamily: "'Inter','Mulish',sans-serif", whiteSpace: "nowrap", pointerEvents: "none", zIndex: 5, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis",
          }}>
            <span style={{ color: hover.color, fontWeight: 700 }}>{hover.label}</span>
            <span style={{ color: isDark ? "#93a4c0" : "#64748b" }}> · {hover.date}</span>
            <div style={{ color: isDark ? "#e6edf6" : "#0f172a", fontWeight: 700, marginTop: 1 }}>Position #{Math.round(hover.pos * 10) / 10}</div>
          </div>
        )}
      </div>
      {series.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 8 }}>
          {series.map(s => {
            const on = !hidden.has(s.key);
            return (
              <label key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, color: on ? (isDark ? "#aeb9cc" : "#475569") : (isDark ? "#5a6b85" : "#a3adba"), cursor: "pointer", userSelect: "none", maxWidth: 320 }} data-tip={on ? `Hide ${s.label}` : `Show ${s.label}`}>
                <input type="checkbox" checked={on} onChange={() => setHidden(prev => { const n = new Set(prev); n.has(s.key) ? n.delete(s.key) : n.add(s.key); return n; })} style={{ width: 13, height: 13, accentColor: s.color, cursor: "pointer", flex: "0 0 auto" }} />
                <span style={{ width: 10, height: 3, borderRadius: 2, background: on ? s.color : (isDark ? "#475569" : "#cbd5e1"), display: "inline-block", flex: "0 0 auto" }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Position-movement indicator: current-period avg vs previous-equal-period avg (lower position =
// better). Improvement (moved up) gets a GREEN box, a drop gets a RED box.
function ChangeCell({ delta, isDark }: { delta: number | null; isDark: boolean }) {
  if (delta === null) return <span style={{ color: isDark ? "#7c8aa0" : "#94a3b8" }}>—</span>;
  if (Math.abs(delta) < 0.05) return <span style={{ color: isDark ? "#93a4c0" : "#64748b" }}>0</span>;
  const improved = delta > 0; // positive delta = position number fell = moved up
  const border = improved ? (isDark ? "rgba(34,197,94,.55)" : "#22c55e") : (isDark ? "rgba(239,68,68,.55)" : "#ef4444");
  const bg = improved ? (isDark ? "rgba(22,163,74,.18)" : "#dcfce7") : (isDark ? "rgba(239,68,68,.18)" : "#fee2e2");
  const color = improved ? (isDark ? "#86efac" : "#166534") : (isDark ? "#fca5a5" : "#991b1b");
  const mag = Math.abs(delta);
  const txt = mag >= 10 ? mag.toFixed(0) : mag.toFixed(1);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 7px", borderRadius: 5, border: `1px solid ${border}`, background: bg, color, fontWeight: 700, fontSize: 11.5 }}
      data-tip={improved ? "Moved up vs. the previous period" : "Dropped vs. the previous period"}>
      {improved ? "▲" : "▼"} {txt}
    </span>
  );
}

// Module-level in-flight run map so a "Run Tracker" survives the component unmounting on tab switch.
type RankRunResult = RankTrackingData & { errors: string[] };
const rankRuns = new Map<string, Promise<RankRunResult>>();
function runStatus(d: RankRunResult): { tone: "info" | "err"; text: string } {
  const snaps = d.snapshots.length;
  if (d.errors.length) return { tone: "err", text: `Recorded rankings, but some keywords errored — ${d.errors.slice(0, 3).join(" · ")}${d.errors.length > 3 ? " …" : ""}` };
  return { tone: "info", text: `Done — refreshed rankings for ${snaps ? "your keywords" : "the tracker"}.` };
}

// ── Date range model ──────────────────────────────────────────
type PresetKey = "2d" | "7d" | "14d" | "30d" | "60d" | "90d" | "6mo" | "12mo" | "prevMonth" | "all";
interface DateRange { from: number; to: number; preset: PresetKey | null }
const PRESETS: { key: PresetKey; label: string }[] = [
  { key: "2d", label: "Last 2 Days" },
  { key: "7d", label: "Last 7 Days" },
  { key: "14d", label: "Last 14 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "60d", label: "Last 60 Days" },
  { key: "90d", label: "Last 90 Days" },
  { key: "6mo", label: "Last 6 Months" },
  { key: "12mo", label: "Last 12 Months" },
  { key: "prevMonth", label: "Previous Month" },
  { key: "all", label: "All Time" },
];
const presetLabelOf = (k: PresetKey) => PRESETS.find(p => p.key === k)?.label ?? "";
const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d: Date) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const fmtRangeDate = (t: number) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

// Compute a preset's {from,to} in ms. `minData` seeds "All Time" (earliest snapshot).
function presetRange(key: PresetKey, minData: number): { from: number; to: number } {
  const now = new Date();
  const to = endOfDay(now).getTime();
  const back = (n: number) => startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - (n - 1))).getTime();
  const monthsBack = (n: number) => startOfDay(new Date(now.getFullYear(), now.getMonth() - n, now.getDate())).getTime();
  switch (key) {
    case "2d": return { from: back(2), to };
    case "7d": return { from: back(7), to };
    case "14d": return { from: back(14), to };
    case "30d": return { from: back(30), to };
    case "60d": return { from: back(60), to };
    case "90d": return { from: back(90), to };
    case "6mo": return { from: monthsBack(6), to };
    case "12mo": return { from: monthsBack(12), to };
    case "prevMonth": {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: startOfDay(first).getTime(), to: endOfDay(last).getTime() };
    }
    case "all": return { from: Math.min(minData, startOfDay(now).getTime()), to };
  }
}

// Date-range control: a labeled trigger showing the current selection, opening a popover with a
// "Preset Date Ranges" dropdown above a from/to calendar. Presets apply instantly; the calendar
// takes a first click (From) then a second click (To) to set a custom range.
function DateRangePicker({ range, minData, onChange, isDark }: { range: DateRange; minData: number; onChange: (r: DateRange) => void; isDark: boolean }) {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(() => new Date(range.to));
  const [pendFrom, setPendFrom] = useState<number | null>(null); // first calendar click, awaiting the To click
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc); document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  useEffect(() => { if (open) { setViewMonth(new Date(range.to)); setPendFrom(null); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const panelBg = isDark ? "#16223a" : "#fff";
  const bdr = isDark ? "rgba(255,255,255,.16)" : "#e2e8f0";
  const ink = isDark ? "#e6edf6" : "#0f172a";
  const ink2 = isDark ? "#93a4c0" : "#64748b";
  const inkMuted = isDark ? "#7c8aa0" : "#94a3b8";
  const accent = "#ea580c";
  const rangeTint = isDark ? "rgba(234,88,12,.22)" : "#ffedd5";
  const fieldBg = isDark ? "#1a2740" : "#fff";

  const valueText = range.preset ? presetLabelOf(range.preset) : `${fmtRangeDate(range.from)} – ${fmtRangeDate(range.to)}`;

  const applyPreset = (key: PresetKey) => { const { from, to } = presetRange(key, minData); onChange({ from, to, preset: key }); setOpen(false); };
  const shiftMonth = (delta: number) => setViewMonth(v => new Date(v.getFullYear(), v.getMonth() + delta, 1));

  const y = viewMonth.getFullYear(), m = viewMonth.getMonth();
  const startWd = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells: (number | null)[] = [...Array(startWd).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const dayMs = (day: number) => startOfDay(new Date(y, m, day)).getTime();
  const dayClick = (day: number) => {
    const t = dayMs(day);
    if (pendFrom == null) { setPendFrom(t); return; }
    if (t >= pendFrom) { onChange({ from: pendFrom, to: endOfDay(new Date(y, m, day)).getTime(), preset: null }); setPendFrom(null); setOpen(false); }
    else setPendFrom(t); // clicked earlier than the start → restart selection here
  };
  const dayState = (day: number): "from" | "to" | "in" | "none" => {
    const t = dayMs(day);
    if (pendFrom != null) return t === pendFrom ? "from" : "none";
    const rf = startOfDay(new Date(range.from)).getTime(), rt = startOfDay(new Date(range.to)).getTime();
    if (t === rf) return "from";
    if (t === rt) return "to";
    return t > rf && t < rt ? "in" : "none";
  };

  const chevBtn: React.CSSProperties = { background: "none", border: `1px solid ${bdr}`, borderRadius: 6, cursor: "pointer", color: ink2, width: 26, height: 26, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14, lineHeight: 1 };

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <span style={{ display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: ink2, marginBottom: 3, textAlign: "right" }}>Date Range</span>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 12px", border: `1px solid ${isDark ? "rgba(255,255,255,.18)" : "#cbd5e1"}`, borderRadius: 6, background: fieldBg, color: ink, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif", minWidth: 190, justifyContent: "space-between" }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
          {valueText}
        </span>
        <span style={{ color: inkMuted, fontSize: 10 }}>▼</span>
      </button>

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 70, width: 288, background: panelBg, border: `1px solid ${bdr}`, borderRadius: 10, boxShadow: isDark ? "0 10px 30px rgba(0,0,0,.55)" : "0 10px 30px rgba(15,23,42,.16)", padding: 12, textAlign: "left" }}>
          {/* Preset dropdown */}
          <label style={{ display: "block", marginBottom: 10 }}>
            <span style={{ display: "block", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: ink2, marginBottom: 4 }}>Preset Date Ranges</span>
            <select
              value={range.preset ?? ""}
              onChange={e => { if (e.target.value) applyPreset(e.target.value as PresetKey); }}
              style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", border: `1px solid ${isDark ? "rgba(255,255,255,.18)" : "#cbd5e1"}`, borderRadius: 6, background: fieldBg, color: ink, fontSize: 12.5, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}
            >
              <option value="" disabled>Choose a preset…</option>
              {PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </label>

          <div style={{ fontSize: 10.5, color: inkMuted, textAlign: "center", margin: "2px 0 8px" }}>— or pick a custom range on the calendar —</div>

          {/* Calendar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <button onClick={() => shiftMonth(-1)} style={chevBtn} aria-label="Previous month">‹</button>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: ink }}>{MONTHS[m]} {y}</span>
            <button onClick={() => shiftMonth(1)} style={chevBtn} aria-label="Next month">›</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {WEEKDAYS.map(w => <div key={w} style={{ textAlign: "center", fontSize: 9.5, fontWeight: 700, color: inkMuted, padding: "2px 0" }}>{w}</div>)}
            {cells.map((day, i) => {
              if (day == null) return <div key={`b${i}`} />;
              const st = dayState(day);
              const bg = st === "from" || st === "to" ? accent : st === "in" ? rangeTint : "transparent";
              const col = st === "from" || st === "to" ? "#fff" : ink;
              return (
                <button key={day} onClick={() => dayClick(day)}
                  style={{ height: 30, border: "none", borderRadius: 6, background: bg, color: col, fontSize: 12, fontWeight: st === "none" ? 500 : 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}
                  onMouseEnter={e => { if (st === "none") e.currentTarget.style.background = isDark ? "rgba(255,255,255,.08)" : "#f1f5f9"; }}
                  onMouseLeave={e => { if (st === "none") e.currentTarget.style.background = "transparent"; }}
                >{day}</button>
              );
            })}
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: ink2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{pendFrom != null ? <><strong style={{ color: ink }}>{fmtRangeDate(pendFrom)}</strong> → <em style={{ color: inkMuted }}>pick end date…</em></> : <>{fmtRangeDate(range.from)} → {fmtRangeDate(range.to)}</>}</span>
            <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: accent, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Page-1 SERP modal: live breakdown of AI Overview / Local Pack / People Also Ask / top 10 organic
// for one keyword. `domain` is the tracked site (its rows are highlighted). Fetched on open.
function SerpModal({ kw, data, loading, error, domain, isDark, onClose, onRefresh }: {
  kw: RankKeyword; data: SerpBreakdown | null; loading: boolean; error: string | null; domain: string;
  isDark: boolean; onClose: () => void; onRefresh: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const panelBg = isDark ? "#0f1b30" : "#fff";
  const bdr = isDark ? "rgba(255,255,255,.14)" : "#e2e8f0";
  const ink = isDark ? "#e6edf6" : "#0f172a";
  const ink2 = isDark ? "#93a4c0" : "#64748b";
  const inkMuted = isDark ? "#7c8aa0" : "#94a3b8";
  const link = isDark ? "#7dd3fc" : "#2563eb";
  const chipBg = isDark ? "rgba(234,88,12,.16)" : "#fff7ed";
  const chipBorder = isDark ? "rgba(234,88,12,.4)" : "#fed7aa";
  const chipInk = isDark ? "#fdba74" : "#c2410c";
  const secBg = isDark ? "rgba(255,255,255,.03)" : "#f8fafc";
  const secBorder = isDark ? "rgba(255,255,255,.10)" : "#e2e8f0";
  const mineBg = isDark ? "rgba(22,163,74,.16)" : "#f0fdf4";
  const mineBorder = isDark ? "rgba(34,197,94,.5)" : "#bbf7d0";
  const cleanDomain = domain.replace(/^www\./, "").toLowerCase();
  const isMine = (d: string) => { const h = d.replace(/^www\./, "").toLowerCase(); return h === cleanDomain || h.endsWith(`.${cleanDomain}`); };

  const sectionHd: React.CSSProperties = { fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: .5, color: ink2, margin: "0 0 7px" };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(2,6,23,.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "5vh 16px", overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(680px, 100%)", background: panelBg, border: `1px solid ${bdr}`, borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.4)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "14px 16px", borderBottom: `1px solid ${bdr}`, background: isDark ? "#16223a" : "#eef2ff" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: .6, color: ink2 }}>Page 1 SERP · Live</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: ink, overflow: "hidden", textOverflow: "ellipsis" }}>{kw.keyword}</div>
            {kw.city && <div style={{ fontSize: 11.5, color: inkMuted }}>searched from {kw.city}</div>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <button onClick={onRefresh} disabled={loading} title="Fetch the SERP again (uses a credit)" style={{ padding: "5px 10px", border: `1px solid ${bdr}`, borderRadius: 6, background: "none", color: ink2, fontSize: 11.5, fontWeight: 700, cursor: loading ? "default" : "pointer" }}>↻ Refresh</button>
            <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: `1px solid ${bdr}`, borderRadius: 6, background: "none", color: ink2, fontSize: 16, cursor: "pointer", lineHeight: 1 }}>×</button>
          </div>
        </div>

        <div style={{ padding: "14px 16px", maxHeight: "72vh", overflowY: "auto" }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 9, color: ink2, fontSize: 13, padding: "18px 0" }}><span className="lsg-spinner" />Fetching the live SERP…</div>
          ) : error ? (
            <div style={{ color: isDark ? "#fca5a5" : "#991b1b", fontSize: 13 }}>{error}</div>
          ) : !data ? null : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Features present */}
              <div>
                <div style={sectionHd}>SERP Features Present</div>
                {data.featured.length === 0 ? <div style={{ fontSize: 12, color: inkMuted }}>No special features — standard organic results only.</div> : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {data.featured.map(f => (
                      <span key={f} style={{ fontSize: 11, fontWeight: 700, color: chipInk, background: chipBg, border: `1px solid ${chipBorder}`, borderRadius: 999, padding: "3px 10px" }}>{featLabel(f)}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* AI Overview */}
              {data.aiOverview && (
                <div style={{ background: secBg, border: `1px solid ${secBorder}`, borderRadius: 8, padding: "10px 12px", fontSize: 12.5, color: ink }}>
                  <strong style={{ color: chipInk }}>AI Overview is present</strong> for this query — Google is showing a generative answer above the results.
                </div>
              )}

              {/* Local Pack */}
              {data.localPack.length > 0 && (
                <div>
                  <div style={sectionHd}>Local Pack ({data.localPack.length})</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {data.localPack.map((b, i) => {
                      const mine = isMine(b.title); // local pack rarely carries a domain; name match is best-effort
                      return (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, background: mine ? mineBg : secBg, border: `1px solid ${mine ? mineBorder : secBorder}` }}>
                          <span style={{ fontWeight: 800, color: ink2, minWidth: 20 }}>{b.position}</span>
                          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.title || "(unnamed)"}</span>
                          {b.rating != null && <span style={{ fontSize: 11.5, color: "#f59e0b", fontWeight: 700, flexShrink: 0 }}>★ {b.rating.toFixed(1)}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* People Also Ask */}
              {data.paa.length > 0 && (
                <div>
                  <div style={sectionHd}>People Also Ask ({data.paa.length})</div>
                  <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 3 }}>
                    {data.paa.map((q, i) => <li key={i} style={{ fontSize: 12.5, color: ink }}>{q}</li>)}
                  </ul>
                </div>
              )}

              {/* Top 10 organic */}
              <div>
                <div style={sectionHd}>Top {Math.min(10, data.organic.length)} Organic Results</div>
                {data.organic.length === 0 ? <div style={{ fontSize: 12, color: inkMuted }}>No organic results returned.</div> : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {data.organic.map((o, i) => {
                      const mine = isMine(o.domain);
                      return (
                        <div key={i} style={{ display: "flex", gap: 9, padding: "7px 10px", borderRadius: 6, background: mine ? mineBg : secBg, border: `1px solid ${mine ? mineBorder : secBorder}` }}>
                          <span style={{ fontWeight: 800, color: mine ? (isDark ? "#86efac" : "#166534") : ink2, minWidth: 22, textAlign: "right" }}>{o.position}</span>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <a href={o.url.startsWith("http") ? o.url : `https://${o.url}`} target="_blank" rel="noopener noreferrer" style={{ color: link, fontSize: 12.5, fontWeight: 600, textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.title}>{o.title || o.url}</a>
                            <div style={{ fontSize: 11, color: mine ? (isDark ? "#86efac" : "#166534") : inkMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.domain}{mine ? " · your site" : ""}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ fontSize: 10.5, color: inkMuted, borderTop: `1px solid ${secBorder}`, paddingTop: 8 }}>Live Google SERP — reflects results right now, on {kw.city ? "the keyword's city" : "the tracker's country"}.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function OrganicRankTracker(props: Props) {
  const key = props.siteUrl ? siteKey(props.siteUrl) : "";

  const [settings, setSettings] = useState<RankTrackerSettings | null>(null);
  const [keywords, setKeywords] = useState<RankKeyword[]>([]);
  const [snapshots, setSnapshots] = useState<RankSnapshot[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState<{ tone: "info" | "err"; text: string } | null>(null);
  const [balance, setBalance] = useState<DfsBalance | null>(null);

  // Add-keyword form. citySel is a DataForSEO location pick (null = country-wide).
  const [newKeyword, setNewKeyword] = useState("");
  const [citySel, setCitySel] = useState<{ name: string; code: number } | null>(null);
  const [countries, setCountries] = useState<RankCountry[]>([]);
  const [recrawling, setRecrawling] = useState<Set<string>>(new Set()); // keyword ids being recrawled
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Page-1 SERP modal (live, on-demand). Cached per keyword so re-opening doesn't re-spend a credit.
  const [serpFor, setSerpFor] = useState<RankKeyword | null>(null);
  const [serpData, setSerpData] = useState<SerpBreakdown | null>(null);
  const [serpLoading, setSerpLoading] = useState(false);
  const [serpErr, setSerpErr] = useState<string | null>(null);
  const serpCache = useRef<Map<string, SerpBreakdown>>(new Map());
  const toggleGraph = useCallback((id: string) => setExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);

  // Overview date range. Drives the chart window AND the current-vs-previous change math. Defaults to
  // Last 30 Days; the user can pick a preset or a custom from/to range via the DateRangePicker.
  const [range, setRange] = useState<DateRange>(() => { const { from, to } = presetRange("30d", Date.now()); return { from, to, preset: "30d" }; });

  const load = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    try {
      const d = await getRankTracking(key);
      setSettings(d.settings); setKeywords(d.keywords); setSnapshots(d.snapshots); setLoaded(true);
    } catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not load tracker data." }); }
    finally { setLoading(false); }
  }, [key]);

  // Clear the previous site's data immediately when the active site changes.
  useEffect(() => { setSettings(null); setKeywords([]); setSnapshots([]); setLoaded(false); setStatus(null); }, [key]);
  useEffect(() => { void load(); }, [load, props.refreshToken]);

  const loadBalance = useCallback(async () => {
    try { setBalance(await rankBalance()); } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { void loadBalance(); }, [loadBalance]);

  // Country library from DataForSEO (strict). Fetched once; the city typeahead is per-country.
  useEffect(() => { let alive = true; (async () => { try { const c = await rankCountries(); if (alive) setCountries(c); } catch { /* fall back to the static list */ } })(); return () => { alive = false; }; }, []);
  // Cities are country-specific — clear the pending pick when the tracker's country changes.
  useEffect(() => { setCitySel(null); }, [settings?.country]);

  // ── Settings mutations (optimistic; server recomputes next_run_at on frequency change) ──
  const patchSettings = useCallback(async (patch: Partial<{ country: string; device: RankDeviceKind; depth: number; frequency: RankFrequency; enabled: boolean }>) => {
    if (!key) { void showAlert("Select a website first (top of the page).", { title: "No Website" }); return; }
    setSettings(prev => prev ? { ...prev, ...patch } as RankTrackerSettings : prev);
    try { const s = await updateRankSettings(key, patch); setSettings(s); }
    catch (e) { void showAlert(e instanceof Error ? e.message : "Could not save settings.", { title: "Error" }); void load(); }
  }, [key, load]);

  const runNow = useCallback(async () => {
    if (running || !key) return;
    if (!keywords.length) { void showAlert("Add at least one keyword first, then Run Tracker.", { title: "No Keywords" }); return; }
    setRunning(true);
    setStatus({ tone: "info", text: "Checking live Google positions…" });
    let p = rankRuns.get(key);
    if (!p) { p = runRankTracking(key); rankRuns.set(key, p); void p.catch(() => {}).finally(() => rankRuns.delete(key)); }
    try {
      const d = await p;
      if (d.settings) setSettings(d.settings);
      setKeywords(d.keywords); setSnapshots(d.snapshots);
      setStatus(runStatus(d));
      void loadBalance();
    } catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Run failed." }); }
    finally { setRunning(false); }
  }, [running, key, keywords.length, loadBalance]);

  // Re-attach to an in-flight run after returning to this tab.
  useEffect(() => {
    if (!key) return;
    const p = rankRuns.get(key);
    if (!p) return;
    let alive = true;
    setRunning(true);
    setStatus({ tone: "info", text: "Checking live Google positions…" });
    p.then(d => { if (alive) { if (d.settings) setSettings(d.settings); setKeywords(d.keywords); setSnapshots(d.snapshots); setStatus(runStatus(d)); } })
      .catch(e => { if (alive) setStatus({ tone: "err", text: e instanceof Error ? e.message : "Run failed." }); })
      .finally(() => { if (alive) setRunning(false); });
    return () => { alive = false; };
  }, [key]);

  const addKeyword = useCallback(async () => {
    // Multiple keywords can be added at once, comma-separated. De-dupe within the input.
    const parts = [...new Set(newKeyword.split(",").map(s => s.trim()).filter(Boolean))];
    if (!key) { void showAlert("Select a website first (top of the page).", { title: "No Website" }); return; }
    if (!parts.length) { void showAlert("Enter a keyword to track (separate multiple with commas).", { title: "Keyword Required" }); return; }
    const city = citySel?.name ?? "";
    const locationCode = citySel?.code ?? null;
    const created: RankKeyword[] = [];
    let failed: unknown = null;
    for (const kw of parts) {
      try { created.push(await addRankKeyword({ siteKey: key, keyword: kw, city, locationCode })); }
      catch (e) { failed = e; }
    }
    if (created.length) setKeywords(prev => [...prev, ...created]);
    if (failed) void showAlert(failed instanceof Error ? failed.message : "Some keywords could not be added.", { title: "Error" });
    else setNewKeyword(""); // keep the city — keywords for the same city are usually added in a batch
  }, [newKeyword, citySel, key]);

  const removeKeyword = useCallback(async (kid: string) => {
    try {
      await deleteRankKeyword(kid);
      setKeywords(prev => prev.filter(k => k.id !== kid));
      setSnapshots(prev => prev.filter(s => s.keywordId !== kid));
    } catch (e) { void showAlert(e instanceof Error ? e.message : "Could not delete keyword.", { title: "Error" }); }
  }, []);

  // Recrawl one keyword now (its own DataForSEO credit); refresh this site's data on return.
  const recrawlKeyword = useCallback(async (kid: string) => {
    if (recrawling.has(kid) || !key) return;
    setRecrawling(prev => new Set(prev).add(kid));
    setStatus(null);
    try {
      const d = await runRankKeyword(kid);
      if (d.settings) setSettings(d.settings);
      setKeywords(d.keywords); setSnapshots(d.snapshots);
      void loadBalance();
    } catch (e) { void showAlert(e instanceof Error ? e.message : "Recrawl failed.", { title: "Recrawl Failed" }); }
    finally { setRecrawling(prev => { const n = new Set(prev); n.delete(kid); return n; }); }
  }, [recrawling, key, loadBalance]);

  // Open the live Page-1 SERP view for a keyword (cached per keyword; `force` re-fetches).
  const openSerp = useCallback(async (k: RankKeyword, force = false) => {
    setSerpFor(k); setSerpErr(null);
    const cached = serpCache.current.get(k.id);
    if (cached && !force) { setSerpData(cached); setSerpLoading(false); return; }
    setSerpData(null); setSerpLoading(true);
    try {
      const d = await rankKeywordSerp(k.id);
      serpCache.current.set(k.id, d);
      setSerpData(d);
      void loadBalance();
    } catch (e) { setSerpErr(e instanceof Error ? e.message : "SERP request failed."); }
    finally { setSerpLoading(false); }
  }, [loadBalance]);
  const closeSerp = useCallback(() => { setSerpFor(null); setSerpData(null); setSerpErr(null); }, []);

  // ── Aggregations ──
  const snapsByKeyword = useMemo(() => {
    const m = new Map<string, RankSnapshot[]>();
    for (const s of snapshots) { const arr = m.get(s.keywordId) ?? []; arr.push(s); m.set(s.keywordId, arr); }
    for (const arr of m.values()) arr.sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
    return m;
  }, [snapshots]);

  // Earliest recorded snapshot (ms) — seeds the "All Time" preset. Fallback: a year back.
  const minData = useMemo(() => {
    let min = Infinity;
    for (const s of snapshots) { const t = Date.parse(s.capturedAt); if (!isNaN(t) && t < min) min = t; }
    return isFinite(min) ? min : Date.now() - 365 * 86400000;
  }, [snapshots]);

  // Overview average-position line: for each day in range, average of each keyword's best position.
  const avgSeries = useMemo<SeriesInput[]>(() => {
    const { from, to } = range;
    // day → list of best positions (one per keyword that has a snapshot that day)
    const byDay = new Map<string, number[]>();
    for (const arr of snapsByKeyword.values()) {
      // latest snapshot per day for this keyword
      const perDay = new Map<string, RankSnapshot>();
      for (const s of arr) { const t = Date.parse(s.capturedAt); if (isNaN(t) || t < from || t > to) continue; const dk = dayKey(s.capturedAt); const cur = perDay.get(dk); if (!cur || s.capturedAt > cur.capturedAt) perDay.set(dk, s); }
      for (const [dk, s] of perDay) { const bp = bestPos(s); if (bp !== null) { const list = byDay.get(dk) ?? []; list.push(bp); byDay.set(dk, list); } }
    }
    const pts = [...byDay.entries()]
      .map(([dk, list]) => ({ t: Date.parse(dk + "T12:00:00"), pos: list.reduce((a, b) => a + b, 0) / list.length, iso: dk }))
      .filter(p => !isNaN(p.t))
      .sort((a, b) => a.t - b.t);
    return pts.length ? [{ key: "avg", label: "Average position", color: AVG_COLOR, pts }] : [];
  }, [snapsByKeyword, range]);

  // Per-keyword change: this period's avg best position vs the previous equal-length period.
  const changeByKeyword = useMemo(() => {
    const { from, to } = range;
    const length = Math.max(1, to - from);
    const prevStart = from - length;
    const m = new Map<string, number | null>();
    for (const [id, arr] of snapsByKeyword) {
      const avg = (a: number, b: number) => {
        const vals = arr.filter(s => { const t = Date.parse(s.capturedAt); return t >= a && t < b; }).map(bestPos).filter((v): v is number => v !== null);
        return vals.length ? vals.reduce((x, y) => x + y, 0) / vals.length : null;
      };
      const cur = avg(from, to + 1);
      const prev = avg(prevStart, from);
      m.set(id, cur !== null && prev !== null ? prev - cur : null); // >0 = improved
    }
    return m;
  }, [snapsByKeyword, range]);

  // Overall average position (mean best position across ALL keywords in the current period) plus the
  // change vs. the previous equal-length period. delta > 0 = improved (moved toward #1).
  const overallAvg = useMemo<{ cur: number | null; delta: number | null }>(() => {
    const { from, to } = range;
    const length = Math.max(1, to - from);
    const prevStart = from - length;
    const meanBest = (a: number, b: number) => {
      const vals: number[] = [];
      for (const arr of snapsByKeyword.values()) {
        for (const s of arr) { const t = Date.parse(s.capturedAt); if (t >= a && t < b) { const bp = bestPos(s); if (bp !== null) vals.push(bp); } }
      }
      return vals.length ? vals.reduce((x, y) => x + y, 0) / vals.length : null;
    };
    const cur = meanBest(from, to + 1);
    const prev = meanBest(prevStart, from);
    return { cur, delta: cur !== null && prev !== null ? prev - cur : null };
  }, [snapsByKeyword, range]);

  // Per-keyword multi-series (each ranking URL = a line over time), for the expandable graph.
  const urlSeriesFor = useCallback((keywordId: string): SeriesInput[] => {
    const arr = snapsByKeyword.get(keywordId) ?? [];
    const byUrl = new Map<string, { t: number; pos: number; iso: string }[]>();
    const order: string[] = [];
    for (const s of arr) {
      const t = Date.parse(s.capturedAt);
      if (isNaN(t)) continue;
      for (const r of s.rankings) {
        if (!byUrl.has(r.url)) { byUrl.set(r.url, []); order.push(r.url); }
        byUrl.get(r.url)!.push({ t, pos: r.position, iso: s.capturedAt });
      }
    }
    return order.map((url, i) => ({ key: url, label: pathLabel(url), color: SERIES_COLORS[i % SERIES_COLORS.length], pts: byUrl.get(url)!.sort((a, b) => a.t - b.t) }));
  }, [snapsByKeyword]);

  const notReady = !key;
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const cardBg = isDark ? "#16223a" : "#fff";
  const cardBorder = isDark ? "rgba(255,255,255,.14)" : "#e2e8f0";
  const hdrBg = isDark ? "#26375a" : "#eef2ff";
  const hdrBorder = isDark ? "rgba(255,255,255,.10)" : "#dbe3ff";
  const boxBg = isDark ? "rgba(255,255,255,.04)" : "#f8fafc";
  const boxBorder = isDark ? "rgba(255,255,255,.12)" : "#e2e8f0";
  const ink = isDark ? "#e6edf6" : "#0f172a";
  const ink2 = isDark ? "#93a4c0" : "#64748b";
  const inkMuted = isDark ? "#7c8aa0" : "#94a3b8";
  const inkLabel = isDark ? "#aeb9cc" : "#475569";
  const inkMid = isDark ? "#cbd5e1" : "#334155";
  const link = isDark ? "#7dd3fc" : "#2563eb";
  const red = isDark ? "#f87171" : "#dc2626";
  const bannerAmberT: React.CSSProperties = { ...bannerBase, background: isDark ? "rgba(245,158,11,.12)" : "#fffbeb", borderColor: isDark ? "rgba(245,158,11,.4)" : "#fcd34d", color: isDark ? "#fcd34d" : "#92400e" };
  const bannerRedT: React.CSSProperties = { ...bannerBase, background: isDark ? "rgba(239,68,68,.12)" : "#fef2f2", borderColor: isDark ? "rgba(239,68,68,.4)" : "#fecaca", color: isDark ? "#fca5a5" : "#991b1b" };
  const help: React.CSSProperties = { fontFamily: "'Inter','Mulish',sans-serif", fontSize: 12, color: ink2, marginTop: 4, lineHeight: 1.5, maxWidth: 760 };
  const inpT: React.CSSProperties = { ...inp, border: `1px solid ${isDark ? "rgba(255,255,255,.18)" : "#cbd5e1"}`, background: isDark ? "#1a2740" : "#fff", color: ink };
  const lbl: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: ink2, marginBottom: 3 };
  const device = settings?.device ?? "desktop";
  const pagesCount = Math.max(1, Math.round((settings?.depth ?? 100) / 10));
  // Country dropdown options: DataForSEO's list when available, else the static fallback list.
  const countryOptions = countries.length ? countries.map(c => ({ name: c.name })) : COUNTRIES.map(c => ({ name: c.locationName }));
  const activeCountry = settings?.country ?? DEFAULT_COUNTRY;

  return (
    <div className="content-drawer" style={{ marginTop: 8 }}>
      <div style={{ padding: "0 9px", marginBottom: 16 }}>
        <div className="cd-help" style={{ ...help, marginTop: 0 }}>
          Tracks your website&rsquo;s Google organic positions for the keywords you add, over time, using live SERP checks.
          Choose the <strong>country</strong> all keywords are searched in, the <strong>device</strong>, how many <strong>pages of Google</strong> to scan,
          and a <strong>check frequency</strong> — the tracker re-checks on that cadence to build a position trend. Each keyword can be searched from its own <strong>city</strong>.
          <br /><br />
          The graph for a keyword plots <strong>every page on your site that ranks for that term</strong> (not just the first found), so you can see when multiple pages compete or trade places over time.
        </div>

        {/* ── Tracker settings bar ── */}
        <div style={{ border: `1px solid ${boxBorder}`, borderRadius: 8, background: boxBg, padding: "12px 14px", marginTop: 12 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" }}>
            <label style={{ flex: "1 1 200px", minWidth: 180 }}>
              <span style={lbl}>Country (all keywords)</span>
              <select value={settings?.country ?? DEFAULT_COUNTRY} disabled={notReady} onChange={e => void patchSettings({ country: e.target.value })} style={{ ...inpT, cursor: "pointer" }}>
                {countryOptions.some(c => c.name === (settings?.country ?? DEFAULT_COUNTRY)) ? null : <option value={settings?.country}>{settings?.country}</option>}
                {countryOptions.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </label>
            <label style={{ flex: "0 0 auto" }}>
              <span style={lbl}>Device</span>
              <div style={{ display: "inline-flex", border: `1px solid ${isDark ? "rgba(255,255,255,.18)" : "#cbd5e1"}`, borderRadius: 6, overflow: "hidden" }}>
                {(["desktop", "mobile"] as RankDeviceKind[]).map(d => (
                  <button key={d} disabled={notReady} onClick={() => void patchSettings({ device: d })}
                    style={{ padding: "7px 14px", border: "none", cursor: notReady ? "default" : "pointer", fontSize: 12.5, fontWeight: 700, textTransform: "capitalize", fontFamily: "'Inter','Mulish',sans-serif", background: device === d ? "linear-gradient(135deg,#ea580c,#f97316)" : (isDark ? "#1a2740" : "#fff"), color: device === d ? "#fff" : inkMid }}>
                    {d}
                  </button>
                ))}
              </div>
            </label>
            <label style={{ flex: "0 0 auto" }}>
              <span style={lbl}>Pages to scan</span>
              <input type="number" min={1} max={10} value={pagesCount} disabled={notReady}
                onChange={e => { const p = Math.max(1, Math.min(10, Math.round(Number(e.target.value) || 1))); void patchSettings({ depth: p * 10 }); }}
                style={{ ...inpT, width: 78 }} title="How many pages of Google results to search (10 results per page)" />
            </label>
            <label style={{ flex: "0 0 auto" }}>
              <span style={lbl}>Check frequency</span>
              <select value={settings?.frequency ?? "weekly"} disabled={notReady} onChange={e => void patchSettings({ frequency: e.target.value as RankFrequency })} style={{ ...inpT, cursor: "pointer" }}>
                <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
              </select>
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button
                onClick={async () => {
                  if (running || notReady) return;
                  const ok = await showConfirm(
                    "This runs a live Google search for every keyword below from its chosen city, on the selected device, and records today's positions. It uses DataForSEO credits. If you've set a check frequency, the tracker already updates automatically — you only need this to see fresh positions right now. Continue?",
                    { title: "Run Tracker", confirmLabel: "Continue", cancelLabel: "Cancel", tone: "info" },
                  );
                  if (ok) void runNow();
                }}
                disabled={running || notReady}
                onMouseEnter={e => { if (!(running || notReady)) { e.currentTarget.style.filter = "brightness(1.08)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(234,88,12,0.35)"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
                onMouseLeave={e => { e.currentTarget.style.filter = ""; e.currentTarget.style.boxShadow = ""; e.currentTarget.style.transform = ""; }}
                style={{ padding: "10px 18px", border: "none", borderRadius: 0, fontFamily: "'Inter','Mulish',sans-serif", fontSize: 13, fontWeight: 700, color: "#fff", cursor: running || notReady ? "default" : "pointer", background: running || notReady ? "#fdba74" : "linear-gradient(135deg,#ea580c,#f97316)", whiteSpace: "nowrap", transition: "filter .15s ease, box-shadow .15s ease, transform .15s ease" }}
              >
                {running ? "Running…" : "Run Tracker"}
              </button>
            </div>
          </div>
          {running && status && (
            <div style={{ fontSize: 12.5, color: status.tone === "err" ? red : ink2, display: "flex", alignItems: "center", gap: 7, marginTop: 10 }}>
              <span className="lsg-spinner" />{status.text}
            </div>
          )}
          {notReady && <div style={{ fontSize: 12.5, fontWeight: 600, color: link, marginTop: 10 }}>Select a website to use this tool.</div>}
        </div>
      </div>

      {status && !running && (
        <div style={{ padding: "0 9px", marginBottom: 16, fontSize: 12.5, color: status.tone === "err" ? red : ink2, display: "flex", alignItems: "center", gap: 7 }}>
          {loading && <span className="lsg-spinner" />}{status.text}
        </div>
      )}

      {/* DataForSEO balance banner */}
      {balance?.configured && balance.ok === false && (
        <div style={bannerRedT}><strong>DataForSEO account issue:</strong> {balance.error || "account unreachable"}. Rank tracking can&rsquo;t run until this is resolved in your DataForSEO account.</div>
      )}
      {balance?.configured && balance.ok && balance.balanceUsd != null && balance.balanceUsd < LOW_BALANCE_USD && (
        <div style={bannerAmberT}><strong>Low DataForSEO balance: {usd(balance.balanceUsd)}.</strong> Rank tracking will stop when credits run out — top up at{" "}
          <a href="https://app.dataforseo.com/" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", fontWeight: 700 }}>dataforseo.com</a>.</div>
      )}
      {balance?.configured && balance.ok && balance.balanceUsd != null && balance.balanceUsd >= LOW_BALANCE_USD && (
        <div style={{ padding: "0 9px", marginBottom: 16, fontSize: 11.5, color: inkMuted }}>DataForSEO balance: {usd(balance.balanceUsd)}</div>
      )}

      {/* ── Overview: average position over the date range ── */}
      <div style={{ padding: "0 9px", marginBottom: 24 }}>
        <div className="cd-section">
          <div className="cd-section-hd" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
              <span className="cd-hd-brand">Overview — Average Position</span>
              {overallAvg.cur !== null && (() => {
                const d = overallAvg.delta;
                const improved = d != null && d > 0.05;   // lower position number = moved up = better
                const declined = d != null && d < -0.05;
                const color = improved ? (isDark ? "#86efac" : "#16a34a") : declined ? (isDark ? "#f87171" : "#dc2626") : ink2;
                const sign = improved ? "+" : declined ? "−" : "";
                return (
                  <span data-tip="Average Google position across all tracked keywords for the selected range (sign/color show the change vs. the previous equal-length period)"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 15, fontWeight: 800, color }}>
                    {sign}#{overallAvg.cur.toFixed(1)}
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: ink2, textTransform: "none" }}>avg position</span>
                  </span>
                );
              })()}
            </span>
            <DateRangePicker range={range} minData={minData} onChange={setRange} isDark={isDark} />
          </div>
          <p className="cd-help">Your site&rsquo;s average Google position across all tracked keywords (position 1 is best — the line is highest when you rank best).</p>
          <div style={{ marginTop: 6 }}>
            <PositionChart series={avgSeries} windowStart={range.from} windowEnd={range.to} />
          </div>
        </div>
      </div>

      {/* ── Keywords ── */}
      <div style={{ padding: "0 9px", marginBottom: 24 }}>
        <div className="cd-section">
          <div className="cd-section-hd"><span className="cd-hd-brand">Tracked Keywords</span></div>
          <p className="cd-help">Positions from the latest check. Add several keywords at once by separating them with <strong>commas</strong>; each is added for the selected <strong>city</strong>. Start typing a city and pick it from the supported list — or leave it blank to search country-wide. The <strong>Pos.&nbsp;Movement</strong> column compares this period&rsquo;s average position to the previous equal-length period — <span style={{ color: isDark ? "#86efac" : "#166534", fontWeight: 700 }}>green</span> means you moved up, <span style={{ color: isDark ? "#fca5a5" : "#991b1b", fontWeight: 700 }}>red</span> means you dropped. Use the <strong>Recrawl</strong> button to re-check a single keyword now. Click a row to see every ranking page over time.</p>

          {/* Add-keyword form */}
          <div style={{ border: `1px solid ${boxBorder}`, borderRadius: 8, background: boxBg, padding: "12px 14px", marginBottom: 14 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
              <label style={{ flex: "2 1 260px", minWidth: 220 }}>
                <span style={lbl}>Keyword(s)</span>
                <input value={newKeyword} onChange={e => setNewKeyword(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); void addKeyword(); } }} placeholder="e.g. teen rehab near me, adolescent iop, detox center" style={inpT} title="Add one keyword, or several separated by commas." />
              </label>
              <label style={{ flex: "1 1 200px", minWidth: 180 }}>
                <span style={lbl}>City (optional)</span>
                <CityPicker country={activeCountry} value={citySel} onChange={setCitySel} isDark={isDark} disabled={notReady} inpStyle={inpT} />
              </label>
              <button onClick={() => void addKeyword()} disabled={notReady} style={{ padding: "8px 16px", border: "none", borderRadius: 6, background: notReady ? "#93c5a3" : "#16a34a", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: notReady ? "default" : "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}>+ Add</button>
            </div>
          </div>

          {keywords.length === 0 ? (
            <p className="cd-help">{loading && !loaded ? "Loading…" : "No keywords yet — add one above, then Run Tracker."}</p>
          ) : (
            <div style={{ overflow: "auto", maxHeight: "70vh" }}>
              <table className="cd-history" style={{ margin: 0, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>Recrawl</th>
                    <th style={{ textAlign: "left" }}>Keyword</th>
                    <th style={{ textAlign: "left" }}>City</th>
                    <th style={{ textAlign: "center" }}>Volume</th>
                    <th style={{ textAlign: "center" }}>CPC</th>
                    <th style={{ textAlign: "center" }}>SF</th>
                    <th style={{ textAlign: "center" }}>Prev.&nbsp;Pos</th>
                    <th style={{ textAlign: "center" }}>Current&nbsp;Pos</th>
                    <th style={{ textAlign: "center" }}>Pos.&nbsp;Movement</th>
                    <th style={{ textAlign: "left" }}>Ranking page(s)</th>
                    <th style={{ textAlign: "center" }}>Last&nbsp;Crawl&nbsp;Date</th>
                    <th style={{ textAlign: "center" }}>Page&nbsp;1&nbsp;SERP</th>
                    <th style={{ textAlign: "center" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {keywords.map(k => {
                    const arr = snapsByKeyword.get(k.id) ?? [];
                    const latest = arr.length ? arr[arr.length - 1] : undefined;
                    const prevSnap = arr.length > 1 ? arr[arr.length - 2] : undefined;
                    const open = expanded.has(k.id);
                    const series = open ? urlSeriesFor(k.id) : [];
                    const ranks = latest?.rankings ?? [];
                    const depthCap = settings?.depth ?? 100;
                    const posCell = (snap: RankSnapshot | undefined) => {
                      if (!snap) return <span style={{ color: inkMuted }}>—</span>;
                      const p = bestPos(snap);
                      return (
                        <div style={{ lineHeight: 1.25 }}>
                          <div style={{ fontWeight: 700, color: p != null ? ink : inkMuted }}>{p != null ? `#${p}` : `>${depthCap}`}</div>
                          <div style={{ fontSize: 10, color: inkMuted, whiteSpace: "nowrap" }}>{fmtRangeDate(Date.parse(snap.capturedAt))}</div>
                        </div>
                      );
                    };
                    return (
                      <Fragment key={k.id}>
                        <tr onClick={() => toggleGraph(k.id)} style={{ cursor: "pointer" }} title="Click to view every ranking page over time">
                          <td style={{ textAlign: "center" }} onClick={e => e.stopPropagation()}>
                            <button onClick={() => void recrawlKeyword(k.id)} disabled={recrawling.has(k.id) || notReady} data-tip="Recrawl this keyword now (uses one DataForSEO credit)" className="rt-tip-left"
                              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: recrawling.has(k.id) || notReady ? "default" : "pointer", color: recrawling.has(k.id) || notReady ? inkMuted : "#ea580c", padding: 2, lineHeight: 0 }}>
                              {recrawling.has(k.id) ? <span className="lsg-spinner" /> : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36" /><polyline points="21 3 21 9 15 9" /></svg>}
                            </button>
                          </td>
                          <td style={{ fontWeight: 600, color: ink }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <span style={{ color: inkMuted, transform: open ? "rotate(90deg)" : "none", transition: "transform .12s", display: "inline-block" }}>▸</span>
                              {k.keyword}
                            </span>
                          </td>
                          <td style={{ color: k.city ? inkMid : inkMuted, whiteSpace: "nowrap" }} data-tip={k.city ? prettyLoc(k.city) : "Country-wide"}>{k.city ? compactLoc(k.city) : "—"}</td>
                          <td style={{ textAlign: "center" }}>{latest?.volume != null ? latest.volume.toLocaleString() : "—"}</td>
                          <td style={{ textAlign: "center" }}>{latest?.cpc != null ? usd(latest.cpc) : "—"}</td>
                          <td style={{ textAlign: "center" }} data-tip={latest?.serpFeatures?.length ? latest.serpFeatures.join(", ") : "No SERP features"}>{latest?.serpFeatures?.length ? latest.serpFeatures.length : "—"}</td>
                          <td style={{ textAlign: "center" }}>{posCell(prevSnap)}</td>
                          <td style={{ textAlign: "center" }}>{posCell(latest)}</td>
                          <td style={{ textAlign: "center" }} onClick={e => e.stopPropagation()}><ChangeCell delta={changeByKeyword.get(k.id) ?? null} isDark={isDark} /></td>
                          <td style={{ maxWidth: 320 }}>
                            {ranks.length === 0 ? <span style={{ color: inkMuted }}>{latest ? "Not ranking" : "—"}</span> : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {ranks.slice(0, 3).map(r => (
                                  <a key={r.url} href={hrefFor(r.url)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} data-tip={r.url} style={{ color: link, fontSize: 11.5, display: "block", maxWidth: "100%", textDecoration: "none" }}>
                                    <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>#{r.position} · {pathLabel(r.url)}</span>
                                  </a>
                                ))}
                                {ranks.length > 3 && <span style={{ color: inkMuted, fontSize: 11 }}>+{ranks.length - 3} more</span>}
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: "center", whiteSpace: "nowrap", color: latest ? inkMid : inkMuted }}>{latest ? fmtRangeDate(Date.parse(latest.capturedAt)) : "—"}</td>
                          <td style={{ textAlign: "center" }} onClick={e => e.stopPropagation()}>
                            <button onClick={() => void openSerp(k)} disabled={notReady} data-tip="Show the live Page 1 SERP (features, local pack, PAA, top 10) for this keyword"
                              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", border: `1px solid ${isDark ? "rgba(255,255,255,.2)" : "#cbd5e1"}`, borderRadius: 6, background: isDark ? "#1a2740" : "#fff", color: notReady ? inkMuted : link, fontSize: 11.5, fontWeight: 700, cursor: notReady ? "default" : "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
                              SERP
                            </button>
                          </td>
                          <td style={{ textAlign: "center" }} onClick={e => e.stopPropagation()}>
                            <button onClick={e => { confirmDeletePopover(e.currentTarget, () => void removeKeyword(k.id), "Remove this keyword and its history?"); }} data-tip="Remove keyword" style={{ background: "none", border: "none", cursor: "pointer", color: red, padding: 2, lineHeight: 0 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                            </button>
                          </td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={13} style={{ background: isDark ? "rgba(255,255,255,.02)" : "#fbfcfe", padding: "12px 14px" }}>
                              <div style={{ fontSize: 11.5, color: ink2, marginBottom: 6 }}>
                                Every page on your site ranking for <strong style={{ color: ink }}>{k.keyword}</strong> over time{k.city ? <> (searched from {k.city})</> : null}. Each line is one URL.
                              </div>
                              <PositionChart series={series} windowStart={range.from} windowEnd={range.to} height={170} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: inkMuted, marginTop: 8 }}>
                Tracker: {FREQ_LABEL[settings?.frequency ?? "weekly"]} · {device} · top {pagesCount} page{pagesCount === 1 ? "" : "s"} of Google · {settings?.country ?? DEFAULT_COUNTRY}
                {settings?.lastRunAt ? <> · last checked {fmtDate(settings.lastRunAt)}</> : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {serpFor && (
        <SerpModal kw={serpFor} data={serpData} loading={serpLoading} error={serpErr} domain={key} isDark={isDark}
          onClose={closeSerp} onRefresh={() => void openSerp(serpFor, true)} />
      )}
    </div>
  );
}

const inp: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 6,
  fontSize: 12.5, fontFamily: "'Inter','Mulish',sans-serif", color: "#0f172a", outline: "none",
};
const bannerBase: React.CSSProperties = {
  margin: "0 9px 16px", padding: "10px 14px", borderRadius: 8, border: "1px solid",
  fontSize: 12.5, lineHeight: 1.5, fontFamily: "'Inter','Mulish',sans-serif",
};
